import React from "react";
import { useApiQuery } from "@/lib/useApiQuery";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";

const DIRECTION = {
  better: { label: "Improving", color: "#156F5B", Icon: TrendingDown },
  worse: { label: "Getting worse", color: "#B91C1C", Icon: TrendingUp },
  flat: { label: "Holding steady", color: "#92400E", Icon: Minus },
};

/** Week-over-week fault totals plus per-site direction. */
export function FaultTrends() {
  const { data, err } = useApiQuery(["faultTrends", 6], "/faults/trends", { params: { weeks: 6 } });

  if (err) return null;
  if (!data) return <div className="mt-6 k-card p-6 k-sublabel" data-testid="fault-trends-loading">Building week-over-week trends…</div>;
  if (!data.weeks?.length) return null;

  const dir = DIRECTION[data.direction] || DIRECTION.flat;
  const peak = Math.max(...data.weeks.map((w) => w.hours), 0.0001);

  return (
    <div className="mt-6 k-card p-6" data-testid="fault-trends">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="k-label">Week over week</div>
          <div className="mt-2 flex items-center gap-2 text-lg font-heading font-semibold" style={{ color: dir.color }} data-testid="fault-trends-direction">
            <dir.Icon className="h-4 w-4" /> {dir.label}
          </div>
          {data.delta_hours != null && (
            <div className="mt-1 text-sm text-[#6B7280]">
              {data.delta_hours > 0 ? "+" : ""}
              {data.delta_hours.toFixed(1)} fault hours vs last week
              {data.delta_faults != null && ` · ${data.delta_faults > 0 ? "+" : ""}${data.delta_faults} faults`}
            </div>
          )}
        </div>

        <div className="flex items-end gap-2 h-20">
          {data.weeks.map((w, i) => (
            <div key={w.start} className="flex flex-col items-center justify-end h-full" title={`${w.faults} faults · ${w.hours.toFixed(1)} hr`}>
              <div
                className="w-7 rounded-t"
                style={{
                  height: `${(w.hours / peak) * 100}%`,
                  minHeight: 4,
                  background: i === data.weeks.length - 1 ? "#156F5B" : "#CBD5D1",
                  transition: "height .5s ease",
                }}
                data-testid={`fault-trend-bar-${i}`}
              />
              <div className="mt-1 text-[9px] text-[#9CA3AF]">
                {new Date(`${w.start}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.site_trends?.length > 0 && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.site_trends.slice(0, 6).map((s) => {
            const d = DIRECTION[s.direction] || DIRECTION.flat;
            return (
              <div key={s.site_id} className="rounded-md border border-[#F0F2F1] p-3 flex items-center justify-between gap-3" data-testid={`fault-trend-site-${s.site_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-heading font-semibold text-[#212121] truncate">{s.site_dis}</div>
                  <div className="text-[11px] text-[#6B7280]">
                    {s.hours.toFixed(1)} hr · was {s.previous_hours.toFixed(1)} hr
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-heading font-semibold shrink-0" style={{ color: d.color }}>
                  <d.Icon className="h-3 w-3" />
                  {s.delta_pct != null ? `${s.delta_pct > 0 ? "+" : ""}${s.delta_pct}%` : "new"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
